/**
 * Shared, session-scoped patient directory for Today, Patients, and quick search.
 *
 * The primary path uses the compact authenticated API projection. A lightweight
 * Firestore metadata fallback keeps mixed-version deployments usable without
 * reintroducing document-subcollection reads.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "./firebase";
import { getAuthHeaders } from "./apiUtils";
import { ServerURI } from "../globals";
import { beginPatientApiLoad, clearPatientApiTimings } from './dashboardPerformance';

const CACHE_TTL_MS = 5 * 60 * 1000;
const stores = new Map();

function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value.toDate === "function") return value.toDate();
  if (typeof value.seconds === "number") return new Date(value.seconds * 1000);
  if (typeof value._seconds === "number") return new Date(value._seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function normalizeSummary(summary) {
  if (!summary) return null;
  return {
    ...summary,
    updatedAt: toDate(summary.updatedAt),
    lastSeenAt: toDate(summary.lastSeenAt),
    firstDocumentDate: toDate(summary.firstDocumentDate),
    lastDocumentDate: toDate(summary.lastDocumentDate),
    nextExpectedDate: toDate(summary.nextExpectedDate),
    severityDateRange: summary.severityDateRange
      ? {
          start: toDate(summary.severityDateRange.start),
          end: toDate(summary.severityDateRange.end),
        }
      : null,
    latestDocument: summary.latestDocument
      ? {
          ...summary.latestDocument,
          createdAt: toDate(summary.latestDocument.createdAt),
          modifiedAt: toDate(summary.latestDocument.modifiedAt),
        }
      : null,
    recentHistory: summary.recentHistory
      ? {
          ...summary.recentHistory,
          createdAt: toDate(summary.recentHistory.createdAt),
        }
      : null,
    attentionItems: (summary.attentionItems || []).map((item) => ({
      ...item,
      startedAt: toDate(item.startedAt),
    })),
  };
}

export function normalizePatientDirectoryRecord(patient) {
  const summary = normalizeSummary(patient?._summary);
  return {
    ...patient,
    createdAt: toDate(patient?.createdAt) || patient?.createdAt || null,
    modifiedAt: toDate(patient?.modifiedAt) || patient?.modifiedAt || null,
    _summary: summary,
    summaryReady: !!summary,
    lastSeenAt: summary?.lastSeenAt || null,
    document: summary?.recentHistory || null,
    latestDocument: summary?.latestDocument || null,
    contactFrequency: summary
      ? {
          averageDaysBetween: summary.averageDaysBetween ?? null,
          frequency: summary.contactFrequency || "infrequent",
          firstDocumentDate: summary.firstDocumentDate,
          lastDocumentDate: summary.lastDocumentDate,
          nextExpectedDate: summary.nextExpectedDate,
        }
      : null,
    overallSeverity: summary
      ? {
          overallSeverityRank: summary.overallSeverityRank ?? null,
          overallSeverityTrend: summary.overallSeverityTrend || "stable",
          overallSeverityAverage: summary.overallSeverityAverage ?? null,
          totalItems: summary.totalItems || 0,
          dateRange: summary.severityDateRange,
          // PatientRow historically consumed `trend`; PatientsList consumes the
          // longer property. Keep both during the migration.
          trend: summary.overallSeverityTrend || "stable",
        }
      : null,
    attentionItems: summary?.attentionItems || [],
  };
}

function getStore(userId) {
  if (!stores.has(userId)) {
    stores.set(userId, {
      patients: [],
      hasLoaded: false,
      isLoading: false,
      error: null,
      updatedAt: 0,
      promise: null,
      listeners: new Set(),
    });
  }
  return stores.get(userId);
}

function snapshot(store) {
  return {
    patients: store.patients,
    hasLoaded: store.hasLoaded,
    isLoading: store.isLoading,
    error: store.error,
    updatedAt: store.updatedAt,
  };
}

function notify(store) {
  const next = snapshot(store);
  store.listeners.forEach((listener) => listener(next));
}

async function fetchFromApi(userId) {
  const finish = beginPatientApiLoad(userId);
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${ServerURI}/api/v3/patients`, { headers });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const error = new Error(body.error || `Patient list request failed (${response.status})`);
      error.status = response.status;
      throw error;
    }
    const body = await response.json();
    finish('success');
    return Array.isArray(body.patients) ? body.patients : [];
  } catch (error) {
    finish('error');
    throw error;
  }
}

async function fetchFromFirestore(userId) {
  const userSnapshot = await getDoc(doc(db, "users", userId));
  if (!userSnapshot.exists()) return [];
  const userData = userSnapshot.data();
  const ownedIds = [...new Set(userData.patientIds || [])];
  const ownedIdSet = new Set(ownedIds);
  const sharesSnapshot = await getDocs(query(
    collection(db, "patientShares"),
    where("sharedWithUserId", "==", userId),
    where("revokedAt", "==", null)
  ));
  const now = new Date();
  const sharesByPatient = new Map();
  sharesSnapshot.docs.forEach((shareSnapshot) => {
    const share = { id: shareSnapshot.id, ...shareSnapshot.data() };
    const expiresAt = toDate(share.expiresAt);
    if (expiresAt && expiresAt <= now) return;
    if (!share.patientId || ownedIdSet.has(share.patientId)) return;
    sharesByPatient.set(share.patientId, share);
  });

  const allIds = [...new Set([...ownedIds, ...sharesByPatient.keys()])];
  const patientSnapshots = await Promise.all(
    allIds.map((patientId) => getDoc(doc(db, "patients", patientId)).catch(() => null))
  );
  const pinPreferences = userData.settings?.patientList?.byPatient || {};
  return patientSnapshots
    .filter((patientSnapshot) => patientSnapshot?.exists())
    .map((patientSnapshot) => {
      const patientId = patientSnapshot.id;
      const patient = patientSnapshot.data();
      const share = sharesByPatient.get(patientId) || null;
      const isShared = !!share && !ownedIdSet.has(patientId);
      const pinPreference = pinPreferences[patientId]?.pinned;
      return {
        id: patientId,
        ...patient,
        ...(typeof pinPreference === "boolean" && { pin: pinPreference }),
        ...(isShared && {
          isShared: true,
          shareInfo: {
            shareId: share.id,
            accessLevel: share.accessLevel,
            ownerId: share.ownerId,
            expiresAt: toDate(share.expiresAt),
          },
        }),
      };
    })
    .filter((patient) => patient.isArchived !== true);
}

export function getPatientDirectorySnapshot(userId) {
  if (!userId) {
    return { patients: [], hasLoaded: true, isLoading: false, error: null, updatedAt: 0 };
  }
  return snapshot(getStore(userId));
}

export function subscribeToPatientDirectory(userId, listener) {
  if (!userId) return () => {};
  const store = getStore(userId);
  store.listeners.add(listener);
  return () => store.listeners.delete(listener);
}

export async function loadPatientDirectory(userId, { force = false } = {}) {
  if (!userId) return [];
  const store = getStore(userId);
  const isFresh = store.hasLoaded && Date.now() - store.updatedAt < CACHE_TTL_MS;
  if (!force && isFresh) return store.patients;
  if (store.promise) return store.promise;

  store.isLoading = true;
  store.error = null;
  notify(store);
  store.promise = (async () => {
    let records;
    try {
      records = await fetchFromApi(userId);
    } catch (apiError) {
      // The fallback exists for mixed-version deployments and transient server
      // failures. Never bypass an explicit authentication/authorization denial.
      if (apiError.status === 401 || apiError.status === 403) throw apiError;
      console.warn("[patientDirectory] API unavailable; using metadata fallback:", apiError.message);
      records = await fetchFromFirestore(userId);
    }
    store.patients = records
      .map(normalizePatientDirectoryRecord)
      .filter((patient) => patient.isArchived !== true);
    store.hasLoaded = true;
    store.updatedAt = Date.now();
    return store.patients;
  })()
    .catch((error) => {
      store.error = error;
      if (!store.hasLoaded) store.patients = [];
      throw error;
    })
    .finally(() => {
      store.isLoading = false;
      store.promise = null;
      notify(store);
    });

  return store.promise;
}

export function patchPatientDirectory(userId, patientId, patch) {
  if (!userId || !patientId) return;
  const store = getStore(userId);
  const wasLoaded = store.hasLoaded;
  const index = store.patients.findIndex((patient) => patient.id === patientId);
  if (index === -1) {
    if (patch?.isArchived === true) return;
    store.patients = [
      ...store.patients,
      normalizePatientDirectoryRecord({ id: patientId, ...patch }),
    ];
  } else if (patch?.isArchived === true) {
    store.patients = store.patients.filter((patient) => patient.id !== patientId);
  } else {
    const merged = { ...store.patients[index], ...patch };
    store.patients = store.patients.map((patient, patientIndex) => (
      patientIndex === index ? normalizePatientDirectoryRecord(merged) : patient
    ));
  }
  // A local mutation must not make a previously-unloaded partial store look
  // complete. The next consumer still needs to fetch the full directory.
  store.updatedAt = wasLoaded ? Date.now() : 0;
  notify(store);
}

export function invalidatePatientDirectory(userId = null) {
  const selectedStores = userId && stores.has(userId)
    ? [[userId, stores.get(userId)]]
    : [...stores.entries()];
  selectedStores.forEach(([selectedUserId, store]) => {
    store.updatedAt = 0;
    if (store.listeners.size > 0) {
      loadPatientDirectory(selectedUserId, { force: true }).catch(() => {});
    }
  });
}

export function clearPatientDirectory(userId = null) {
  clearPatientApiTimings(userId);
  if (userId) stores.delete(userId);
  else stores.clear();
}

export async function createQuickPatient(firstName, lastName, creationId) {
  const response = await fetch(`${ServerURI}/api/v3/patients`, {
    method: "POST",
    headers: await getAuthHeaders(),
    body: JSON.stringify({ firstName, lastName, quickAdd: true, creationId }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `Patient creation failed (${response.status})`);
  }
  const { patientId, patient } = await response.json();
  return { id: patientId, ...patient };
}

export async function setPatientPinPreference(userId, patientId, pinned) {
  if (!userId || !patientId) throw new Error("userId and patientId are required");
  await updateDoc(doc(db, "users", userId), {
    [`settings.patientList.byPatient.${patientId}.pinned`]: pinned,
  });
  patchPatientDirectory(userId, patientId, { pin: pinned });
}

export async function refreshPatientSummary(userId, patientId) {
  const headers = await getAuthHeaders();
  const response = await fetch(`${ServerURI}/api/v3/patients/${patientId}/summary`, {
    method: "PATCH",
    headers,
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `Summary refresh failed (${response.status})`);
  }
  const body = await response.json();
  patchPatientDirectory(userId, patientId, { _summary: body.summary });
  return body.summary;
}
